import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { useTranslations } from '@/hooks/useTranslations';
import { router } from '@inertiajs/react';
import { FormEventHandler, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface AddSettingItemDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    settingKey: string;
    settingType: 'string' | 'object' | 'value_label';
    onSuccess?: () => void;
    translationGroup?: string | null;
}

export function AddSettingItemDialog({ open, onOpenChange, settingKey, settingType, onSuccess, translationGroup }: AddSettingItemDialogProps) {
    const [value, setValue] = useState('');
    const [label, setLabel] = useState('');
    const [color, setColor] = useState('');
    const [enLabel, setEnLabel] = useState('');
    const [ltLabel, setLtLabel] = useState('');
    const [processing, setProcessing] = useState(false);
    const { t } = useTranslations();

    const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();
        setProcessing(true);

        let itemData: string | { value: string; label?: string; color?: string };

        if (settingType === 'string') {
            itemData = value;
        } else if (settingType === 'value_label') {
            itemData = { value, label: label || value };
        } else {
            // object type with optional color
            itemData = { value, label: label || value };
            if (color) {
                itemData.color = color;
            }
        }

        router.post(
            route('settings.system.add-item', settingKey),
            {
                item: itemData,
                translations: translationGroup
                    ? {
                          en: enLabel || label || value,
                          lt: ltLabel || label || value,
                      }
                    : undefined,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    toast.success(t('toasts.settings_addsettingitemdialog.item_added', 'Item added successfully'));
                    setValue('');
                    setLabel('');
                    setColor('');
                    setEnLabel('');
                    setLtLabel('');
                    onOpenChange(false);
                    onSuccess?.();
                },
                onError: (errors) => {
                    toast.error(errors.item?.[0] || t('toasts.settings_addsettingitemdialog.add_failed', 'Failed to add item'));
                },
                onFinish: () => {
                    setProcessing(false);
                },
            },
        );
    };

    const handleClose = () => {
        if (!processing) {
            setValue('');
            setLabel('');
            setColor('');
            setEnLabel('');
            setLtLabel('');
            onOpenChange(false);
        }
    };

    return (
        <ResponsiveModal open={open} onOpenChange={handleClose} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>Add New Item</ResponsiveModalTitle>
                    <ResponsiveModalDescription>Add a new item to {settingKey.replace(/([A-Z])/g, ' $1').trim()}</ResponsiveModalDescription>
                </ResponsiveModalHeader>
                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="value">Value *</Label>
                            <Input
                                id="value"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder="Enter value (e.g., new_status)"
                                required
                                disabled={processing}
                            />
                            <p className="text-muted-foreground text-xs">This is the internal value used in the system</p>
                        </div>

                        {(settingType === 'value_label' || settingType === 'object') && (
                            <div className="space-y-2">
                                <Label htmlFor="label">Label</Label>
                                <Input
                                    id="label"
                                    value={label}
                                    onChange={(e) => setLabel(e.target.value)}
                                    placeholder="Enter display label (optional)"
                                    disabled={processing}
                                />
                                <p className="text-muted-foreground text-xs">Display label (defaults to value if not provided)</p>
                            </div>
                        )}

                        {settingType === 'object' && (
                            <div className="space-y-2">
                                <Label htmlFor="color">Color</Label>
                                <Input
                                    id="color"
                                    type="color"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    disabled={processing}
                                    className="h-10"
                                />
                                <p className="text-muted-foreground text-xs">Optional color for status/priority indicators</p>
                            </div>
                        )}

                        {translationGroup && (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="enLabel">English Translation</Label>
                                    <Input
                                        id="enLabel"
                                        value={enLabel}
                                        onChange={(e) => setEnLabel(e.target.value)}
                                        placeholder="Enter English translation"
                                        disabled={processing}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="ltLabel">Lithuanian Translation</Label>
                                    <Input
                                        id="ltLabel"
                                        value={ltLabel}
                                        onChange={(e) => setLtLabel(e.target.value)}
                                        placeholder="Enter Lithuanian translation"
                                        disabled={processing}
                                    />
                                </div>
                            </>
                        )}
                    </ResponsiveModalBody>
                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={handleClose} disabled={processing}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={processing || !value}>
                            {processing ? 'Adding...' : 'Add Item'}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
